import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

function formatCompactNumber(value) {
    if (typeof value !== 'number') return value;
    if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
    if (value >= 1e3) return (value / 1e3).toFixed(0) + 'k';
    return String(value);
}

// Bar-in-bar: background track (rangeBar) + foreground value (bar)
export default function BarInBarChart({ rows = [], xKey, yAxisKeys = [], theme = {}, onExposeLegend }) {
    const isHorizontal = theme?.orientation === 'horizontal';
    const maxLen = Number(theme?.categoryMaxLen) > 0 ? Number(theme.categoryMaxLen) : 26;
    const rawCategories = useMemo(() => Array.isArray(rows) ? rows.map(r => r?.[xKey]) : [], [rows, xKey]);
    const categories = useMemo(() => rawCategories.map(n => typeof n === 'string' ? (n.length > maxLen ? n.slice(0, maxLen - 1) + '…' : n) : n), [rawCategories, maxLen]);

    const { trackColor, barColor } = {
        trackColor: theme.trackColor || 'rgba(148, 163, 184, 0.25)', // slate-400 @ 25%
        barColor: theme.barColor || '#4F46E5',
        ...theme
    };

    const { baseKey, valueKey } = useMemo(() => {
        const k1 = Array.isArray(yAxisKeys) && yAxisKeys.length > 0 ? yAxisKeys[0] : undefined;
        const k2 = Array.isArray(yAxisKeys) && yAxisKeys.length > 1 ? yAxisKeys[1] : undefined;
        return { baseKey: k1, valueKey: k2 || k1 };
    }, [yAxisKeys]);

    const baseValues = useMemo(() => {
        if (baseKey) return rows.map(r => Number(r?.[baseKey]) || 0);
        const allVals = rows.map(r => Object.values(r).filter(v => typeof v === 'number'));
        const max = Math.max(0, ...allVals.flat().map(n => Number(n) || 0));
        return rows.map(() => max);
    }, [rows, baseKey]);

    const valueValues = useMemo(() => rows.map(r => Number(r?.[valueKey]) || 0), [rows, valueKey]);

    // Implementation note:
    // Use a single bar series with ApexCharts' backgroundBar to render the grey track.
    // This avoids mixed-type rendering quirks and ensures the foreground bar color shows correctly in all orientations.
    const series = useMemo(() => ([
        {
            name: String(valueKey || 'Value'),
            data: categories.map((x, i) => ({ x, y: valueValues[i] || 0 })),
            color: barColor
        }
    ]), [categories, valueValues, barColor, valueKey]);

    const options = useMemo(() => ({
        chart: { type: 'bar', stacked: false, parentHeightOffset: 0, toolbar: { show: false } },
        plotOptions: { bar: {
            horizontal: isHorizontal,
            columnWidth: '55%',
            borderRadius: 6,
            dataLabels: { position: isHorizontal ? 'right' : 'top' },
            colors: {
                backgroundBarColors: [trackColor],
                backgroundBarOpacity: 0.25,
                backgroundBarRadius: 6
            }
        } },
        legend: {
            show: theme?.legend === false ? false : (theme?.legend === true ? true : (typeof theme?.legend?.show === 'boolean' ? theme.legend.show : true)),
            position: typeof theme?.legend?.position === 'string' ? theme.legend.position : 'top',
            horizontalAlign: typeof theme?.legend?.horizontalAlign === 'string' ? theme.legend.horizontalAlign : 'left',
            onItemClick: { toggleDataSeries: true },
            onItemHover: { highlightDataSeries: true },
            markers: { fillColors: [barColor] }
        },
        dataLabels: {
            enabled: theme?.showBarValues !== false,
            offsetY: isHorizontal ? 0 : -10,
            offsetX: isHorizontal ? 12 : 0,
            formatter: val => `${formatCompactNumber(val)}`,
            style: { fontWeight: 500, colors: ['#94a3b8'], fontSize: '12px' }
        },
        tooltip: { enabled: true },
        grid: { show: Boolean(theme?.showGridX || theme?.showGridY), xaxis: { lines: { show: Boolean(theme?.showGridX) } }, yaxis: { lines: { show: Boolean(theme?.showGridY) } }, padding: isHorizontal ? { top: (theme?.showBarValues !== false ? 16 : 12), left: 32, right: 8, bottom: 0 } : { top: (theme?.showBarValues !== false ? 22 : 12), left: 0, right: 0, bottom: -8 } },
        stroke: { width: 0 },
        xaxis: isHorizontal ? {
            title: { text: theme.xAxisTitle || undefined, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showXAxisLine) },
            labels: { show: theme?.showXAxisLabels !== false, formatter: val => `${formatCompactNumber(Number(val))}`, style: { colors: '#9ca3af', fontSize: '12px' } }
        } : {
            categories,
            title: { text: theme.xAxisTitle || undefined, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showXAxisLine) },
            labels: { show: theme?.showXAxisLabels !== false, rotate: -35, trim: false, hideOverlappingLabels: false, style: { colors: '#9ca3af', fontSize: '12px' } }
        },
        yaxis: isHorizontal ? {
            categories,
            title: { text: theme.yAxisTitle || undefined, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showYAxisLine) },
            labels: { show: theme?.showYAxisLabels !== false, offsetX: 0, maxWidth: 220, style: { colors: '#9ca3af', fontSize: '12px' } }
        } : {
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showYAxisLine) },
            labels: { show: theme?.showYAxisLabels !== false, formatter: val => `${formatCompactNumber(val)}`, style: { colors: '#9ca3af', fontSize: '12px' } }
        },
        states: { hover: { filter: { type: 'none' } }, active: { filter: { type: 'none' } } }
    }), [categories, isHorizontal, theme]);

    // Expose legend items after render commit (single series)
    const exposedItems = useMemo(() => ([{ label: String(valueKey || 'Value'), color: barColor }]), [valueKey, barColor]);
    React.useEffect(() => {
        if (typeof onExposeLegend === 'function') {
            onExposeLegend(exposedItems);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(exposedItems)]);

    return (
        <Chart
            type="bar"
            height={300}
            series={series}
            options={options}
        />
    );
}


